import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  API_BASE_URL,
  CDN_BASE_URL,
  CDN_FETCH_BASE_URL,
  PLACE_IMAGE_CACHE_BUST,
  PLACE_UI_FROZEN,
} from '../config';
import { getSubredditFromUrl, marketingHomeUrl } from '../utils/subreddit';
import { formatSubredditPlaceTitle, placeBrowsePath, placeLeaderboardPath, placeTimelapsePath } from '../utils/placePaths';
import { placeLivePlacePngUrlWithBust, placeSixtyFortyFlairUrls } from '../utils/placeCdnUrls';
import {
  PLACE_PALETTE_RGBA,
  PLACE_LABELS,
  PLACE_WIDTH,
  PLACE_HEIGHT,
  rgbaToNearestIndex,
} from '../constants/placePalette';

function decodePixels(base64: string, width: number, height: number): Uint8Array {
  const bin = atob(base64);
  const out = new Uint8Array(width * height);
  for (let i = 0; i < out.length; i++) {
    out[i] = bin.charCodeAt(i) & 0xff;
  }
  return out;
}

/**
 * Public CDN assets (same keys as backend place seed / place_cdn_sync).
 * When the API is down, CDN fallback uses flairCode as a stem guess (best-effort vs image_source).
 */
async function loadPixelsFromCdn(
  cdnBase: string,
  subreddit: string,
  stemGuess: string
): Promise<{ pixels: Uint8Array; width: number; height: number }> {
  const urls = placeSixtyFortyFlairUrls(cdnBase, subreddit, stemGuess);
  let lastErr: Error | null = null;
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const blob = await res.blob();
      const bmp = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas unavailable');
      ctx.drawImage(bmp, 0, 0);
      const img = ctx.getImageData(0, 0, bmp.width, bmp.height);
      const pixels = new Uint8Array(bmp.width * bmp.height);
      const d = img.data;
      for (let i = 0; i < pixels.length; i++) {
        const o = i * 4;
        pixels[i] = rgbaToNearestIndex(d[o], d[o + 1], d[o + 2], d[o + 3]);
      }
      return { pixels, width: bmp.width, height: bmp.height };
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e));
    }
  }
  throw lastErr ?? new Error('No CDN image found');
}

export default function PlacePage() {
  const navigate = useNavigate();
  const params = useParams<{ subreddit?: string; flairCode: string }>();
  const flairCode = (params.flairCode || '').toLowerCase();
  const subreddit = (params.subreddit || getSubredditFromUrl() || 'cfb').toLowerCase();

  const { user, login, isLoading: authLoading } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cdnPreview, setCdnPreview] = useState(false);
  const [width, setWidth] = useState(PLACE_WIDTH);
  const [height, setHeight] = useState(PLACE_HEIGHT);
  const [pixels, setPixels] = useState<Uint8Array | null>(null);
  const [selectedColor, setSelectedColor] = useState(1);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownMs, setCooldownMs] = useState(5000);
  const [nowTick, setNowTick] = useState(Date.now());
  /** Master sheet flair_abbreviated (API); falls back to flair code. */
  const [teamLabel, setTeamLabel] = useState('');
  const [cdnStem, setCdnStem] = useState('');
  const [placeFrozen, setPlaceFrozen] = useState(PLACE_UI_FROZEN);
  const [cdnCacheBust, setCdnCacheBust] = useState<string | null>(null);

  useEffect(() => {
    setTeamLabel(flairCode);
  }, [flairCode]);

  const remainingMs = Math.max(0, cooldownUntil - nowTick);

  useEffect(() => {
    if (remainingMs <= 0) return;
    const t = setInterval(() => setNowTick(Date.now()), 200);
    return () => clearInterval(t);
  }, [remainingMs]);

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c || !pixels) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const img = ctx.createImageData(width, height);
    for (let i = 0; i < width * height; i++) {
      const idx = pixels[i];
      const [r, g, b, a] = PLACE_PALETTE_RGBA[idx] ?? PLACE_PALETTE_RGBA[0];
      const o = i * 4;
      img.data[o] = r;
      img.data[o + 1] = g;
      img.data[o + 2] = b;
      img.data[o + 3] = a;
    }
    ctx.putImageData(img, 0, 0);
    ctx.save();
    ctx.strokeStyle = 'rgba(0,0,0,0.08)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= width; x++) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y <= height; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
    ctx.restore();
  }, [pixels, width, height]);

  useEffect(() => {
    draw();
  }, [draw]);

  /**
   * D1 eligibility is enforced by the API (master_sheet). CDN fallback only runs when the browser
   * cannot reach the API at all — never on 404, so arbitrary slugs cannot load a public CDN PNG
   * after the server rejects them.
   */
  const loadState = useCallback(async () => {
    setLoading(true);
    setError(null);

    const applyApiSuccess = (data: {
      width?: number;
      height?: number;
      pixelsBase64?: string;
      cooldownMs?: number;
      nextPixelAtMs?: number;
      serverNowMs?: number;
      flairAbbreviated?: string;
      cdnStem?: string;
      placeFrozen?: boolean;
      cdnCacheBust?: string | null;
    }) => {
      setCdnPreview(false);
      setCdnStem(typeof data.cdnStem === 'string' ? data.cdnStem : '');
      setPlaceFrozen(PLACE_UI_FROZEN || Boolean(data.placeFrozen));
      setCdnCacheBust(data.cdnCacheBust != null ? String(data.cdnCacheBust) : null);
      if (typeof data.flairAbbreviated === 'string' && data.flairAbbreviated.trim()) {
        setTeamLabel(data.flairAbbreviated.trim());
      } else {
        setTeamLabel(flairCode);
      }
      const w = data.width ?? PLACE_WIDTH;
      const h = data.height ?? PLACE_HEIGHT;
      setWidth(w);
      setHeight(h);
      setCooldownMs(Number(data.cooldownMs) || 5000);
      setPixels(decodePixels(data.pixelsBase64!, w, h));
      const next = Number(data.nextPixelAtMs) || 0;
      const serverNow = Number(data.serverNowMs) || Date.now();
      if (next > serverNow) {
        setCooldownUntil(next);
      } else {
        setCooldownUntil(0);
      }
    };

    const tryCdnFallback = async () => {
      const { pixels: px, width: w, height: h } = await loadPixelsFromCdn(
        CDN_FETCH_BASE_URL,
        subreddit,
        flairCode
      );
      setCdnPreview(true);
      setWidth(w);
      setHeight(h);
      setPixels(px);
      setCooldownMs(5000);
      setCooldownUntil(0);
      setError(null);
    };

    try {
      const headers: Record<string, string> = {};
      if (user?.username) {
        headers.Authorization = `Bearer ${user.username}`;
      }
      const res = await fetch(`${API_BASE_URL}/place/${subreddit}/${encodeURIComponent(flairCode)}`, {
        headers,
      });
      const text = await res.text();
      let data: {
        width?: number;
        height?: number;
        pixelsBase64?: string;
        cooldownMs?: number;
        nextPixelAtMs?: number;
        serverNowMs?: number;
        flairAbbreviated?: string;
        cdnStem?: string;
        placeFrozen?: boolean;
        cdnCacheBust?: string | null;
        error?: string;
      } = {};
      if (text) {
        try {
          data = JSON.parse(text) as typeof data;
        } catch {
          /* non-JSON body */
        }
      }

      if (!res.ok) {
        if (res.status === 404) {
          setCdnPreview(false);
          setPixels(null);
          setError(
            data.error ||
              'This team is not available for Place here (D1 only, or check spelling).'
          );
          return;
        }
        throw new Error(data.error || res.statusText || 'Request failed');
      }

      applyApiSuccess(data);
    } catch (e: unknown) {
      const isLikelyNetworkFailure =
        e instanceof TypeError ||
        (e instanceof Error &&
          (e.message === 'Failed to fetch' ||
            e.message.includes('NetworkError') ||
            e.message.includes('Load failed')));
      if (isLikelyNetworkFailure) {
        try {
          await tryCdnFallback();
        } catch {
          setCdnPreview(false);
          setPixels(null);
          setError(
            e instanceof Error
              ? e.message
              : 'Could not reach the API and CDN preview failed.'
          );
        }
      } else {
        setCdnPreview(false);
        setPixels(null);
        setError(e instanceof Error ? e.message : 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  }, [flairCode, subreddit, user?.username]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  /** Light polling when the live API is available (CDN-only preview has no push updates) */
  useEffect(() => {
    if (cdnPreview || placeFrozen) return;
    const id = setInterval(() => {
      void loadState();
    }, 4000);
    return () => clearInterval(id);
  }, [loadState, cdnPreview, placeFrozen]);

  const placePixel = async (x: number, y: number) => {
    if (placeFrozen || cdnPreview || !user?.username || remainingMs > 0 || !pixels) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/place/${subreddit}/${encodeURIComponent(flairCode)}/pixel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.username}`,
          },
          body: JSON.stringify({ x, y, colorIndex: selectedColor }),
        }
      );
      const data = await res.json();
      if (res.status === 429) {
        setCooldownUntil(Number(data.nextPixelAtMs) || Date.now() + 5000);
        return;
      }
      if (!res.ok) {
        throw new Error(data.error || res.statusText);
      }
      if (data.pixelsBase64) {
        setPixels(decodePixels(data.pixelsBase64, width, height));
      }
      setCooldownUntil(Number(data.nextPixelAtMs) || Date.now() + 5000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not place pixel');
    }
  };

  const onCanvasPointer = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (placeFrozen || cdnPreview || !pixels || remainingMs > 0 || !user) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const scaleX = rect.width / width;
    const scaleY = rect.height / height;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const lx = (clientX - rect.left) / scaleX;
    const ly = (clientY - rect.top) / scaleY;
    const x = Math.floor(lx);
    const y = Math.floor(ly);
    if (x < 0 || x >= width || y < 0 || y >= height) return;
    void placePixel(x, y);
  };

  const backPath = subreddit === 'cfb' && getSubredditFromUrl() ? '/' : `/${subreddit}`;
  const titleSub = formatSubredditPlaceTitle(subreddit);
  const bust = (cdnCacheBust ?? PLACE_IMAGE_CACHE_BUST) || undefined;
  const showFinalCdnImage = placeFrozen && !!cdnStem;
  const finalImageSrc = showFinalCdnImage
    ? placeLivePlacePngUrlWithBust(CDN_BASE_URL, subreddit, cdnStem, bust)
    : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', pb: 4 }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <IconButton edge="start" onClick={() => navigate(backPath)} size="small" aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1, flex: 1, fontWeight: 600, minWidth: 0 }}>
            /r/{titleSub} Place — {teamLabel || flairCode}
          </Typography>
          <Button size="small" component={RouterLink} to={placeBrowsePath(subreddit)} sx={{ flexShrink: 0 }}>
            Browse
          </Button>
          <Button size="small" component={RouterLink} to={placeLeaderboardPath(subreddit)} sx={{ flexShrink: 0 }}>
            Leaderboard
          </Button>
          <Button size="small" component={RouterLink} to={placeTimelapsePath(subreddit)} sx={{ flexShrink: 0 }}>
            Timelapse
          </Button>
          {!user && (
            <Button startIcon={<LoginIcon />} onClick={login} disabled={authLoading} size="small">
              Log in
            </Button>
          )}
          {user && (
            <Chip size="small" label={user.username} variant="outlined" sx={{ maxWidth: 140 }} />
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {cdnPreview && !error && !placeFrozen && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Showing the public CDN snapshot (API unreachable). Pixel placement and live cooldown require the
            backend; refresh after the server is up to use the full Place experience.
          </Alert>
        )}

        {placeFrozen && !error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Place is closed — showing the final <code>_place.png</code> image
            {bust ? ` (cache bust: ${bust})` : ''}.
          </Alert>
        )}

        {!placeFrozen && !user && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Log in with Reddit to place one pixel every few seconds (shared cooldown across all Place canvases
            for this subreddit).
          </Alert>
        )}

        {!placeFrozen && user && remainingMs > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Next pixel in {(remainingMs / 1000).toFixed(1)}s
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, ((cooldownMs - remainingMs) / cooldownMs) * 100)}
              sx={{ mt: 0.5, borderRadius: 1 }}
            />
          </Box>
        )}

        <Paper elevation={3} sx={{ width: '100%', p: 2, bgcolor: 'background.paper', boxSizing: 'border-box' }}>
          {loading ? (
            <Box
              sx={{
                width: '100%',
                aspectRatio: `${width} / ${height}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="text.secondary">Loading canvas…</Typography>
            </Box>
          ) : showFinalCdnImage && finalImageSrc ? (
            <Box
              sx={{
                width: '100%',
                aspectRatio: `${width} / ${height}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
              }}
            >
              <Box
                component="img"
                src={finalImageSrc}
                alt={`${teamLabel || flairCode} Place`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  imageRendering: 'pixelated',
                  display: 'block',
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                aspectRatio: `${width} / ${height}`,
              }}
            >
              <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{
                  width: '100%',
                  height: '100%',
                  imageRendering: 'pixelated',
                  touchAction: 'none',
                  cursor:
                    placeFrozen || cdnPreview || !user || remainingMs > 0 ? 'not-allowed' : 'crosshair',
                  display: 'block',
                }}
                onMouseDown={(e) => onCanvasPointer(e)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  onCanvasPointer(e);
                }}
              />
            </Box>
          )}
        </Paper>

        {!placeFrozen && (
          <Paper sx={{ mt: 2, p: 2, width: '100%', boxSizing: 'border-box' }} elevation={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Color
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                gap: 1,
              }}
            >
              {PLACE_PALETTE_RGBA.map((rgba, i) => {
                const isEraser = i === 0;
                const selected = selectedColor === i;
                return (
                  <Tooltip key={i} title={PLACE_LABELS[i] || `Color ${i}`}>
                    <Button
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() => setSelectedColor(i)}
                      sx={{
                        minHeight: 48,
                        borderStyle: isEraser ? 'dashed' : 'solid',
                        bgcolor: isEraser ? 'action.hover' : `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${rgba[3] / 255})`,
                        borderColor: selected ? 'primary.main' : 'divider',
                      }}
                    >
                      {isEraser ? '∅' : ''}
                    </Button>
                  </Tooltip>
                );
              })}
            </Box>
          </Paper>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {placeFrozen
            ? 'Final canvas image from the CDN. '
            : 'Canvas updates on Reddit when the scheduled job runs (default every few minutes). '}
          <Button size="small" href={marketingHomeUrl()} sx={{ verticalAlign: 'baseline', p: 0, minWidth: 0 }}>
            FlairWizard home
          </Button>
        </Typography>
      </Box>
    </Box>
  );
}
